import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { AngularFireStorage } from "@angular/fire/compat/storage";
import * as moment from "moment";
import { BehaviorSubject, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { Patient } from "../models/patient.model";
import * as utils from '../utils/utility-functions';
import { AngularFireFunctions } from "@angular/fire/compat/functions";

@Injectable({
    providedIn: 'root',
})
export class PatientService {

    private patient = new BehaviorSubject(new Patient);
    currentPatient = this.patient.asObservable();

    private patientList = new BehaviorSubject([]);
    currentPatientList = this.patientList.asObservable();

    private patientHistory = new BehaviorSubject([]);
    currentPatientHistory = this.patientHistory.asObservable();



    constructor(
        private angularFirestore: AngularFirestore,
        private angularFunctions: AngularFireFunctions,
        private storage: AngularFireStorage,
        private afAuth: AngularFireAuth
    ) {
    }

    // return an observable of the patient
    getPatientById(patientID: string) {
        return this.afAuth.authState.pipe(
            switchMap(user => {
                if (user) {
                    return this.angularFirestore.collection<Patient>('Patients').doc(patientID).valueChanges({ idField: 'id' });
                } else {
                    return of(null);
                }
            })
        );
    }

    private async findPatientByID(patientID: string) {
        try {
            let patient = null;
            let patientHistory = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Patients');

            await collection.ref
                .doc(patientID)
                .get()
                .then(querySnapshot => {
                    const docData = {
                        id: querySnapshot.id,
                        ...querySnapshot.data()
                    }
                    patient = docData;
                }).catch(err => console.log("findPatientByID query 1 error: " + err));


            // get subcollection History, if it exists and not empty save to patient history
            await collection.ref
                .doc(patientID)
                .collection('History')
                .get()
                .then(querySnapshot => {
                    if (!querySnapshot.empty) {
                        querySnapshot.forEach(doc => {
                            const docData = {
                                id: doc.id,
                                ...doc.data()
                            }
                            patientHistory.push(docData);
                        });
                    }
                }).catch(err => console.log("findPatientByID query 2 error: " + err));

            // sort patientHistory by timestamp, ASC
            patientHistory.sort((a, b) => {
                return a.timestamp - b.timestamp;
            });

            this.updatePatientHistory(patientHistory);

            this.updatePatient(patient);
            return patient;
        } catch (error) {
            console.log('findPatientByID(): ' + JSON.stringify(error));
        }
    }

    async findPatientByEmail(patientEmail: string) {
        try {
            let patients = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Patients');

            await collection.ref
                .where('email', '==', patientEmail)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        patients.push(docData)
                    });
                }).catch(err => console.log("findPatientByEmail query 1 error: " + err));

            return patients;
        } catch (error) {
            console.log('findPatientByEmail(): ' + JSON.stringify(error));
        }
    }

    async findPatients(searchablePatient) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Patients');

            let queries: any[] = [];
            let patients = [];

            for (const key in searchablePatient) {
                if (typeof searchablePatient[key] != 'undefined' && searchablePatient[key]) {
                    const newQuery = {
                        "key": key,
                        "value": searchablePatient[key]
                    }
                    queries.push(newQuery);
                }
            }

            switch (queries.length) {
                case 1: {
                    await collection.ref
                        .where(queries[0]["key"], '==', queries[0]["value"])
                        .get()
                        .then(querySnapshot => {
                            querySnapshot.forEach(doc => {
                                const docData = {
                                    id: doc.id,
                                    ...doc.data()
                                }
                                patients.push(docData)
                            });
                        }).catch(err => console.log("findPatients query 1 error: " + err));
                    break;
                }
                case 2: {
                    await collection.ref
                        .where(queries[0]["key"], '==', queries[0]["value"])
                        .where(queries[1]["key"], '==', queries[1]["value"])
                        .get()
                        .then(querySnapshot => {
                            querySnapshot.forEach(doc => {
                                const docData = {
                                    id: doc.id,
                                    ...doc.data()
                                }
                                patients.push(docData)
                            });
                        }).catch(err => console.log("findPatients query 2 error: " + err));
                    break;
                }
                case 3: {
                    await collection.ref
                        .where(queries[0]["key"], '==', queries[0]["value"])
                        .where(queries[1]["key"], '==', queries[1]["value"])
                        .where(queries[2]["key"], '==', queries[2]["value"])
                        .get()
                        .then(querySnapshot => {
                            querySnapshot.forEach(doc => {
                                const docData = {
                                    id: doc.id,
                                    ...doc.data()
                                }
                                patients.push(docData)
                            });
                        }).catch(err => console.log("findPatients query 3 error: " + err));
                    break;
                }
                case 4: {
                    await collection.ref
                        .where(queries[0]["key"], '==', queries[0]["value"])
                        .where(queries[1]["key"], '==', queries[1]["value"])
                        .where(queries[2]["key"], '==', queries[2]["value"])
                        .where(queries[3]["key"], '==', queries[3]["value"])
                        .get()
                        .then(querySnapshot => {
                            querySnapshot.forEach(doc => {
                                const docData = {
                                    id: doc.id,
                                    ...doc.data()
                                }
                                patients.push(docData)
                            });
                        }).catch(err => console.log("findPatients query 4 error: " + err));
                    break;
                }
                case 5: {
                    await collection.ref
                        .where(queries[0]["key"], '==', queries[0]["value"])
                        .where(queries[1]["key"], '==', queries[1]["value"])
                        .where(queries[2]["key"], '==', queries[2]["value"])
                        .where(queries[3]["key"], '==', queries[3]["value"])
                        .where(queries[4]["key"], '==', queries[4]["value"])
                        .get()
                        .then(querySnapshot => {
                            querySnapshot.forEach(doc => {
                                const docData = {
                                    id: doc.id,
                                    ...doc.data()
                                }
                                patients.push(docData)
                            });
                        }).catch(err => console.log("findPatients query 5 error: " + err));
                    break;
                }
            }

            this.updatePatientList(patients);
            return patients;
        } catch (error) {
            console.log('findPatients(): ' + JSON.stringify(error));
        }
    }

    async updatePatientAsync(patientUpdated: any) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Patients');
            // const currentPatient = { ...this.patient.getValue() };
            // console.log('current paitnet meciacl history', currentPatient.medical_history);


            let { id, ...patientToSave } = patientUpdated;
            // console.log('patient to save medical history', patientToSave.medical_history);

            // // Log the differences between currentPatient and patientToSave
            // const changes = this.getChanges(currentPatient, patientToSave);

            // console.log('Changes:', changes);

            patientToSave.updated_at = moment().format('x');

            await collection.ref
                .doc(id)
                .update(JSON.parse(JSON.stringify(patientToSave)))
                .catch(err => console.log("updatePatientAsync save 1 error: " + err));

            patientToSave.id = id;
            const savedPatient = utils.createPatient(patientToSave)
            this.updatePatient(patientToSave);
            return savedPatient;

            // return null;
        } catch (error) {
            console.log(error);
        }
    }

    // Helper function to get changes between two objects
    getChanges(oldObj: any, newObj: any): any {
        const changes: any = {};

        Object.keys(newObj).forEach((key) => {
            const oldValue = oldObj[key];
            const newValue = newObj[key];

            // Check if the values are different, considering '1' and 1 as equal
            if (String(oldValue) !== String(newValue)) {
                // If the values are objects, perform a deep equality check
                if (typeof oldValue === 'object' && typeof newValue === 'object') {
                    if (!this.deepEqual(oldValue, newValue)) {
                        changes[key] = {
                            oldValue: oldValue,
                            newValue: newValue,
                        };
                    }
                } else {
                    // Values are not objects, perform a regular equality check
                    changes[key] = {
                        oldValue: oldValue,
                        newValue: newValue,
                    };
                }
            }
        });

        return changes;
    }

    deepEqual(obj1: any, obj2: any): boolean {
        if (obj1 === obj2) {
            return true;
        }

        if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
            return false;
        }

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }

        return true;
    }



    async createPatient(newPatient: any) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Patients');

            let patient = utils.createNewPatientObject(newPatient);
            patient.created_at = moment().format('x');
            patient.updated_at = moment().format('x');

            await collection.ref
                .add(JSON.parse(JSON.stringify(patient)))
                .then(function (docRef) {
                    patient.id = docRef.id;
                })
                .catch(err => console.log("createPatient query 1 error: " + err));

            const savedPatient = utils.createPatient(patient)
            this.updatePatient(patient);
            return savedPatient;
        } catch (error) {
            console.log('createPatient(): ' + JSON.stringify(error));
        }
    }

    async getAllPatients() {
        try {
            let patients = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Patients');

            await collection.ref
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        patients.push(docData)
                    });
                }).catch(err => console.log("getAllPatients query 1 error: " + err));

            this.updatePatientList(patients);
            return patients;
        } catch (error) {
            console.log(error);
        }
    }

    async saveImage(imageFile: any) {
        try {
            let downloadURL;

            const fileName = moment().format('x').toString() + '_' + imageFile.name;
            const remotePath = 'uploads/images/' + fileName;
            const fileRef = this.storage.ref(remotePath)

            await this.storage.upload(remotePath, imageFile)
                .catch((error) => {
                    console.log('saveImage upload error', error)
                });

            await fileRef.getDownloadURL().toPromise()
                .then((result) => {
                    downloadURL = result;
                })
                .catch((error) => {
                    console.log('saveImage getDownloadURL error', error)
                });

            return downloadURL;
        } catch (error) {
            console.log(error);
        }
    }

    async sendDocusign(patientID: any, patientEmail: any, selectedLanguage: any, doctorID: any) {
        try {
            const callable = this.angularFunctions.httpsCallable('sendDocusign');

            callable({
                email: patientEmail,
                doctor_id: doctorID,
                patient_id: patientID,
                language: selectedLanguage
            })
                .toPromise()
                .then((result) => console.log('test', result))
                .catch((error) => console.log('test', error));
        } catch (error) {
            console.log('sendDocusign(): ' + JSON.stringify(error));
        }
    }

    updatePatientList(patientCollectionResults: any) {
        let patients: Patient[] = [];
        for (let i = 0; i < patientCollectionResults.length; i++) {
            let newPatient = utils.createPatient(patientCollectionResults[i]);
            patients.push(newPatient);
        }
        this.patientList.next(patients);
    }

    updatePatientHistory(patientHistoryCollectionResults: any) {

        let updatedPatientHistory = [];
        // loop through patientHistoryCollectionResults and create a new patientHistory object
        // use moment to format the timestamp to MMM DD, YYYY h:mm:ss a
        for (let i = 0; i < patientHistoryCollectionResults.length; i++) {
            let newPatientHistory = {
                ...patientHistoryCollectionResults[i],
                timestamp: moment(patientHistoryCollectionResults[i].timestamp, 'x').format('MMM DD, YYYY h:mm A')
            }
            updatedPatientHistory.push(newPatientHistory);
        }

        this.patientHistory.next(updatedPatientHistory);
    }

    updatePatient(patient: any) {
        let newPatient = utils.createPatient(patient);
        this.patient.next(newPatient);
    }

    setPatientId(patientId: string) {
        this.findPatientByID(patientId);
    }


}